export const USER_ROLE = {
  USER: "user",
  DEVELOPER: "developer",
  ADMIN: "admin",
} as const;

export type UserRole = (typeof USER_ROLE)[keyof typeof USER_ROLE];

export const DEVSPACE_ROLES = [USER_ROLE.ADMIN, USER_ROLE.DEVELOPER] as const;

export type DevspaceRole = (typeof DEVSPACE_ROLES)[number];

export const GAME_ENTRY_SCOPE = {
  PUBLIC: "public",
  STAGING: "staging",
  PRIVATE: "private",
} as const;

export type GameEntryScope =
  (typeof GAME_ENTRY_SCOPE)[keyof typeof GAME_ENTRY_SCOPE];

export const GAME_PLATFORM = {
  UNITY: "unity",
  JAVASCRIPT: "js",
} as const;

export type GamePlatform = (typeof GAME_PLATFORM)[keyof typeof GAME_PLATFORM];

export const GAME_PLATFORMS = Object.values(GAME_PLATFORM) as GamePlatform[];

export const GAME_CATEGORIES = {
  DEV: "dev",
  WIP: "wip",
  COMBAT: "combat",
  RACING: "racing",
  DRIVING: "driving",
} as const;

export type GameCategory =
  (typeof GAME_CATEGORIES)[keyof typeof GAME_CATEGORIES];

/**
 * Categories that are internal-only and must not be offered as choices when
 * authoring a game entry. Existing entries may still carry these values, and
 * they stay valid everywhere that reads, displays, or validates stored data —
 * only the create/edit selectors hide them.
 */
export const INTERNAL_GAME_CATEGORIES = [GAME_CATEGORIES.DEV] as const;

/**
 * Categories a user may pick when authoring a game entry: the full set minus
 * internal-only categories. Any category selector must map over this list, never
 * the full GAME_CATEGORIES or the DB enum, so unsupported categories cannot slip
 * into new selectors.
 */
export const AUTHORABLE_GAME_CATEGORIES = (
  Object.values(GAME_CATEGORIES) as GameCategory[]
).filter(
  (category) =>
    !(INTERNAL_GAME_CATEGORIES as readonly GameCategory[]).includes(category),
);

export const BUILD_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  ARCHIVED: "archived",
} as const;

export type BuildStatus = (typeof BUILD_STATUS)[keyof typeof BUILD_STATUS];

export const BUILD_PROCESSING_STATUS = {
  PENDING: "pending",
  UPLOADING: "uploading",
  EXTRACTING: "extracting",
  PROCESSING: "processing",
  COMPLETED: "completed",
  FAILED: "failed",
} as const;

export type BuildProcessingStatus =
  (typeof BUILD_PROCESSING_STATUS)[keyof typeof BUILD_PROCESSING_STATUS];
